import { useCallback, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import {
  DOT_FIELDS,
  DOT_NAME_RE,
  EMPTY_QUICK_PREFS,
  QUICK_GROUPS,
  QUICK_GROUP_LABEL,
  QUICK_SIDE_CARDS,
  cardsHiddenFor,
  kindCardsHiddenFor,
  quickLibrary,
  quickOrdered,
  type DotPhrase,
  type MyChange,
  type NotePhrasesView,
  type QuickAfter,
  type QuickGroup,
  type QuickItem,
  type QuickPrefs,
  type QuickSideCard,
} from '@ehr/shared';
import { api, ApiError } from '../lib/api.js';
import { isBuiltInRating, moveShown } from '../lib/adminPhoneNote.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { Modal } from './Modal.js';
import { dotListOf } from '../lib/dotPhrases.js';
import { quickLabel } from '../lib/noteLayouts.js';
import {
  allClientsShape,
  quickSaveShape,
  sideCardsAllShape,
  sideCardsSaveShape,
  withShown,
  type SideCardsTab,
} from '../lib/quickSettings.js';
import { timeOpts } from '../lib/timePrefs.js';
import { RailFoldBtn, type RailFold } from './RailFold.js';

/** The cog (V81 COG_SVG). */
export const Cog = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

/**
 * ehr#437 part 4 (PROGRESS_NOTE_10-14-V81 Quick add, V50 / V75 / V76): the note rail's Quick add
 * card. Ratings, checklists and boxes to drop into this note (a green pill is on the note; a click
 * takes it off), the dot phrases you can type (".mse"), and the settings: what is on every note
 * (for this client or all clients) and your own items.
 */
export function QuickAddCard({
  view,
  onChange,
  contactId,
  couples,
  onNote,
  onToggle,
  onSettings,
  fold,
  ownRatingsOnly = false,
}: {
  view: NotePhrasesView | null;
  onChange: (v: NotePhrasesView) => void;
  contactId: string;
  /** Relationship satisfaction is offered on a couples note only. */
  couples: boolean;
  /** The Quick add sections on this note. */
  onNote: string[];
  onToggle: (it: QuickItem) => void;
  /** ehr#682 (V141 V93): the cog opens Note settings on its Quick add tab. */
  onSettings: () => void;
  /** ehr#437 reopen (V81 rail): folds to its header unless Quick add is set to stay open. */
  fold?: RailFold;
  /** ehr#724 (ADMIN_PHONE_NOTE_10-30-V7 V5): Ratings offers only your own (no built-in ones). */
  ownRatingsOnly?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [group, setGroup] = useState<QuickGroup>('ratings');
  const list = view ? dotListOf(view.practice, view.mine) : [];
  // ehr#478: in the order dragged in Quick add settings
  const items = view
    ? quickOrdered(view.quick ?? EMPTY_QUICK_PREFS, group).filter(
        (i) => (i.id !== 'r_relsat' || couples) && !(ownRatingsOnly && isBuiltInRating(i)),
      )
    : [];
  // V81 qaClientOwn: this client has Quick add settings of their own
  const ownTag = !!view?.quick?.clients?.[contactId];
  const shut = !!fold && !fold.open;
  return (
    <section
      className={`pn-card pn-quick-card${shut ? ' pn-rail-shut' : ''}`}
      aria-label="Quick add"
    >
      <div className="pn-rail-h pn-quick-h">
        <h3>Quick add</h3>
        {ownTag && <span className="pn-tag">This client</span>}
        <span className="pn-sp" />
        {fold && <RailFoldBtn fold={fold} title="Quick add" />}
        <button
          type="button"
          className="pn-cog"
          disabled={!view}
          aria-label="Quick add settings"
          title="Quick add settings"
          onClick={onSettings}
        >
          <Cog />
        </button>
      </div>
      {!shut && (
        <>
          <div className="pn-seg pn-quick-groups" role="tablist" aria-label="Quick add group">
            {QUICK_GROUPS.map((g) => (
              <button
                key={g}
                type="button"
                role="tab"
                aria-pressed={group === g}
                aria-selected={group === g}
                onClick={() => setGroup(g)}
              >
                {QUICK_GROUP_LABEL[g]}
              </button>
            ))}
          </div>
          <div className="pn-pills pn-quick-pills">
            {items.map((it) => {
              const on = onNote.includes(it.id);
              return (
                <button
                  key={it.id}
                  type="button"
                  className={`pn-pill${on ? ' on' : ''}`}
                  aria-pressed={on}
                  title={on ? 'On this note. Click to take it off.' : 'Add to this note'}
                  onClick={() => onToggle(it)}
                >
                  {on ? '✓ ' : '+ '}
                  {it.label}
                </button>
              );
            })}
          </div>
          <p className="pn-quick-help">
            Type a dot and a name in any box, like <b>.mse</b>, then Enter. Tab goes to the next __
            blank.
          </p>
          {list.length > 0 && (
            <div className="pn-pills">
              {list.slice(0, 14).map((d) => (
                <span key={d.name} className="pn-dot-name" title={d.text}>
                  .{d.name}
                </span>
              ))}
              {list.length > 14 && <span className="pn-dot-name more">+{list.length - 14}</span>}
            </div>
          )}
          <button
            type="button"
            className="btn pn-quick-manage"
            disabled={!view}
            onClick={() => setOpen(true)}
          >
            Manage dot phrases
          </button>
        </>
      )}
      {open && view && (
        <DotPhrasesWindow
          view={view}
          onClose={() => setOpen(false)}
          onSaved={(v) => {
            onChange(v);
          }}
        />
      )}
    </section>
  );
}

const ONE: Record<QuickGroup, string> = {
  ratings: 'rating',
  checklists: 'checklist',
  boxes: 'box',
};
const AFTER_LABEL: Record<QuickAfter, string> = {
  plan: 'After Plan',
  assessment: 'After Assessment',
  '@first': 'After the first box',
  '@phrase': 'After Objective (Data, Intervention)',
};
/** ehr#478 (V81 QA_TABS): the item groups, then Dot phrases. */
type QsTab = QuickGroup | 'dot';
const QS_TABS: [QsTab, string][] = [
  ...QUICK_GROUPS.map((g) => [g, QUICK_GROUP_LABEL[g]] as [QsTab, string]),
  ['dot', 'Dot phrases'],
];
/** V81 quickDetail: the line under an item's name. */
function quickDetail(it: QuickItem): string {
  if (it.group === 'ratings') return `${it.min ?? 0} to ${it.max ?? 10}`;
  if (it.group === 'checklists') {
    const n = it.items?.length ?? 0;
    return `${n} item${n === 1 ? '' : 's'}`;
  }
  return 'Box';
}
const GRIP = (
  <svg width="10" height="15" viewBox="0 0 12 18" fill="currentColor" aria-hidden>
    <circle cx="3" cy="3" r="1.6" />
    <circle cx="9" cy="3" r="1.6" />
    <circle cx="3" cy="9" r="1.6" />
    <circle cx="9" cy="9" r="1.6" />
    <circle cx="3" cy="15" r="1.6" />
    <circle cx="9" cy="15" r="1.6" />
  </svg>
);
interface QsForm {
  /** The own item being edited; null adds one. */
  id: string | null;
  label: string;
  items: string;
  after: QuickAfter;
  min: string;
  max: string;
}

/**
 * Quick add settings (V75 to V77; ehr#478 brings the V81 window): a tab per group, then Dot
 * phrases. Each tab has a search, Select all / Deselect all, and its items in the order dragged
 * (the grip, or its arrow keys), each Built-in or Mine, with Edit and Delete on your own. A checked
 * item is on every new note: Save for this client keeps the list for this client (a client's own
 * list replaces the all-clients one), Save for all clients makes it the all-clients list. The
 * footer also has Use all-clients settings and Reset to practice list. Closing with changes not
 * saved asks first. V81's other tabs (Problems and goals addressed, Fields, Phrases, Layout) aren't
 * built yet.
 *
 * ehr#682 (PROGRESS-NOTE-10-20-V141 V104, V129, V130): it is Note settings' Quick add tab now (the
 * window's title follows the tab, V112). At the top: Show Quick add (saved as soon as it is
 * clicked, V129), then Show Measures, Show Last session, Show My changes and Show Generate Notes,
 * saved with Save for this client / Save for all clients. The Hide Quick add button is gone.
 */
export function QuickSettingsWindow({
  view,
  contactId,
  contactName,
  couples,
  onClose,
  onApplied,
  onSaved,
  tabs,
  leaveRef,
  offCards = [],
  ownRatingsOnly = false,
  noMeasures = false,
  railOff = [],
  dotOff = false,
  sideCards,
}: {
  view: NotePhrasesView;
  contactId: string;
  contactName: string;
  couples: boolean;
  /** ehr#724 (ADMIN_PHONE_NOTE_10-30-V7 V5): Ratings lists only your own (no built-in ones). */
  ownRatingsOnly?: boolean;
  /** ehr#724: the note has no Measures card, so no Show Measures switch. */
  noMeasures?: boolean;
  /**
   * ehr#725 (CASE_MANAGEMENT_NOTE_10-30-V9 16 A): the Show switches this kind of note doesn't
   * offer ('quick' is Show Quick add); what is saved for them stays as it was.
   */
  railOff?: readonly string[];
  /** ehr#725 (V9 18 A): no Dot phrases tab (the smaller Quick add tab of a case management note). */
  dotOff?: boolean;
  onClose: () => void;
  /** Saved without closing (Use all-clients settings, a dot phrase, Show Quick add). */
  onApplied: (v: NotePhrasesView, added: QuickItem[]) => void;
  onSaved: (v: NotePhrasesView, added: QuickItem[]) => void;
  /** ehr#682: Note settings' tab strip, shown above this tab. */
  tabs?: ReactNode;
  /** ehr#682: Note settings asks here before leaving the tab (changes not saved ask first). */
  leaveRef?: MutableRefObject<((go: () => void) => void) | null>;
  /** ehr#708: the side cards this note doesn't have (a discharge summary), so no Show switch. */
  offCards?: readonly string[];
  /**
   * ehr#728 (COLLATERAL_CONTACT_NOTE_10-30-V5 Side cards): a note with no Quick add. The window is
   * "Side cards": only these cards' Show switches, saved for this note kind only (for this client
   * or all clients); no Show Quick add, no lists, and the Quick add settings stay as they are.
   */
  sideCards?: SideCardsTab;
}) {
  // what is saved; Use all-clients settings changes it with the window open
  const [base, setBase] = useState<QuickPrefs>(view.quick ?? EMPTY_QUICK_PREFS);
  const [draft, setDraft] = useState<QuickPrefs>(base);
  const forClient = (d: QuickPrefs) => d.clients[contactId] ?? d.onNote;
  // ehr#437 (V77, Chase 10-16): one list, saved for this client or for all clients
  const [ids, setIds] = useState<string[]>(forClient(base));
  // ehr#682 (V141 V130): the rail cards switched off, saved with the list; ehr#728: on a Side
  // cards tab, that note kind's own
  const savedCards = (d: QuickPrefs) =>
    sideCards ? kindCardsHiddenFor(d, contactId, sideCards.kind) : cardsHiddenFor(d, contactId);
  const [cards, setCards] = useState<QuickSideCard[]>(savedCards(base));
  // where "Don't save" goes (closing the window, or another Note settings tab)
  const [ask, setAsk] = useState<(() => void) | null>(null);
  const [discard, setDiscard] = useState<{ title: string; go: () => void } | null>(null);
  const [tab, setTab] = useState<QsTab>('ratings');
  const [q, setQ] = useState('');
  const [form, setForm] = useState<QsForm | null>(null);
  const [del, setDel] = useState<string | null>(null);
  const [drag, setDrag] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const first = contactName.split(' ')[0] ?? contactName;
  const group: QuickGroup = tab === 'dot' ? 'ratings' : tab;
  const lib = quickOrdered(draft, group).filter(
    (i) => (i.id !== 'r_relsat' || couples) && !(ownRatingsOnly && isBuiltInRating(i)),
  );
  const query = q.trim().toLowerCase();
  const rows = lib.filter((it) => !query || it.label.toLowerCase().includes(query));
  const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
  const orderOf = (d: QuickPrefs) =>
    Object.fromEntries(Object.entries(d.order ?? {}).filter(([, l]) => l && l.length));
  const formTyped = !!form && (form.label.trim() !== '' || form.items.trim() !== '');
  // V77: closing with changes not saved asks first
  const changed =
    !same(ids, forClient(base)) ||
    !same(draft.mine, base.mine) ||
    !same(orderOf(draft), orderOf(base)) ||
    !same([...cards].sort(), [...savedCards(base)].sort());
  const dirty = changed || formTyped;
  const leave = (go: () => void) => {
    if (busy || discard) return;
    if (dirty) setAsk(() => go);
    else go();
  };
  const requestClose = () => leave(onClose);
  if (leaveRef) leaveRef.current = leave;
  const pickTab = (t: QsTab) => {
    setTab(t);
    setQ('');
    setForm(null);
    setDel(null);
    setErr('');
    setNote('');
  };

  // ── own items: add and edit (V81 quickFormItem) ──
  const openForm = (it?: QuickItem) =>
    setForm(
      it
        ? {
            id: it.id,
            label: it.label,
            items: (it.items ?? []).join('\n'),
            after: it.after ?? 'plan',
            min: String(it.min ?? 0),
            max: String(it.max ?? 10),
          }
        : {
            id: null,
            label: q.trim() && !lib.some((x) => x.label.toLowerCase() === query) ? q.trim() : '',
            items: '',
            after: group === 'boxes' ? 'plan' : '@phrase',
            min: '0',
            max: '10',
          },
    );
  // the form as an item, or what is wrong with it
  const formItem = (f: QsForm): { it: QuickItem } | { err: string } => {
    const label = f.label.replace(/\s+/g, ' ').trim();
    if (!label) return { err: 'Give it a name.' };
    if (
      quickLibrary(draft).some(
        (i) => i.group === group && i.id !== f.id && i.label.toLowerCase() === label.toLowerCase(),
      )
    )
      return { err: `There is already a ${ONE[group]} named ${label}.` };
    const items = f.items
      .split('\n')
      .map((x) => x.trim().slice(0, 120))
      .filter(Boolean);
    if (group === 'checklists' && !items.length)
      return { err: 'Add at least one item, one a line.' };
    const min = Number(f.min);
    const max = Number(f.max);
    if (
      group === 'ratings' &&
      !(Number.isInteger(min) && Number.isInteger(max) && min >= 0 && max <= 10 && min < max)
    )
      return { err: 'A rating runs from 0 to 10, the low number under the high one.' };
    if (!f.id && draft.mine.length >= 30) return { err: 'You can have up to 30 of your own.' };
    return {
      it: {
        id: f.id ?? `my_${Date.now().toString(36)}`,
        group,
        label: label.slice(0, 60),
        ...(group === 'checklists' ? { items: items.slice(0, 15) } : {}),
        ...(group === 'ratings' ? { min, max } : { after: f.after }),
      },
    };
  };
  const withItem = (d: QuickPrefs, it: QuickItem): QuickPrefs => ({
    ...d,
    mine: d.mine.some((x) => x.id === it.id)
      ? d.mine.map((x) => (x.id === it.id ? it : x))
      : [...d.mine, it],
  });
  // The form's Add / Save; a typed form is also added by Save and by changing tabs.
  const saveForm = (): QuickPrefs | null => {
    if (!form) return draft;
    const r = formItem(form);
    if ('err' in r) {
      setErr(r.err);
      return null;
    }
    const next = withItem(draft, r.it);
    setDraft(next);
    setForm(null);
    setQ('');
    setErr('');
    setNote(form.id ? '' : `"${quickLabel(r.it)}" added. Save to keep it.`);
    return next;
  };
  const deleteOwn = (id: string) => {
    setDraft((d) => ({
      ...d,
      mine: d.mine.filter((x) => x.id !== id),
      onNote: d.onNote.filter((x) => x !== id),
      clients: Object.fromEntries(
        Object.entries(d.clients).map(([c, l]) => [c, l.filter((x) => x !== id)]),
      ),
      ...(d.order
        ? {
            order: Object.fromEntries(
              Object.entries(d.order).map(([g, l]) => [g, (l ?? []).filter((x) => x !== id)]),
            ),
          }
        : {}),
    }));
    setIds((l) => l.filter((x) => x !== id));
    setDel(null);
  };

  // ── order: drag a row by its grip, or its arrow keys (V81 wireQuickDrag) ──
  const moveTo = (id: string, to: number) => {
    // ehr#724: rows this note doesn't list (built-in ratings on an Admin / phone note) keep their places
    const order = moveShown(
      quickOrdered(draft, group).map((i) => i.id),
      lib.map((i) => i.id),
      id,
      to,
    );
    if (!order) return;
    setDraft((d) => ({ ...d, order: { ...(d.order ?? {}), [group]: order } }));
  };
  const selectAll = (on: boolean) => {
    const tabIds = lib.map((i) => i.id);
    setIds((l) => (on ? [...new Set([...l, ...tabIds])] : l.filter((x) => !tabIds.includes(x))));
  };

  // ── saving ──
  // V77: Save for this client keeps ${first}'s own list; Save for all clients makes it the all-clients
  // list and ${first} follows it again (other clients' own lists stay theirs). A typed form is added first.
  // ehr#682: the Show switches go the same way; Show Quick add stays as it was saved
  // ehr#728: a Side cards tab saves only its note kind's switches, never the lists
  const save = (to: 'client' | 'all') => {
    setAsk(null);
    const from = formTyped ? saveForm() : draft;
    if (!from) return;
    put(
      (d) =>
        sideCards
          ? sideCardsSaveShape(d, { to, contactId, kind: sideCards.kind, cards })
          : quickSaveShape(d, { to, contactId, ids, cards, shown: !base.hidden }),
      (v, added) => onSaved(v, added),
      from,
    );
  };
  const put = (
    shape: (d: QuickPrefs) => QuickPrefs,
    done: (v: NotePhrasesView, added: QuickItem[]) => void,
    from: QuickPrefs,
  ) => {
    setBusy(true);
    setErr('');
    const before = forClient(base);
    api
      .put<NotePhrasesView>('/note-phrases/quick-add', shape(from))
      .then((v) => {
        const now = forClient(v.quick);
        const lib2 = quickLibrary(v.quick);
        done(
          v,
          now
            .filter((id) => !before.includes(id))
            .map((id) => lib2.find((i) => i.id === id))
            .filter((i): i is QuickItem => !!i),
        );
        return v;
      })
      .catch((e: unknown) =>
        setErr(
          e instanceof ApiError
            ? e.message
            : `Could not save ${sideCards ? 'the side cards' : 'Quick add settings'}.`,
        ),
      )
      .finally(() => setBusy(false));
  };
  // V81 qaUseAll: this client goes back to the all-clients settings (saved now); unsaved changes are
  // asked about first. ehr#728: on a Side cards tab, that note kind's all-clients cards only.
  const useAll = () => {
    const go = () => {
      setDiscard(null);
      put(
        (d) =>
          sideCards
            ? sideCardsAllShape(d, contactId, sideCards.kind)
            : allClientsShape(d, contactId),
        (v, added) => {
          const b = v.quick ?? EMPTY_QUICK_PREFS;
          setBase(b);
          setDraft(b);
          setIds(forClient(b));
          setCards(savedCards(b));
          setForm(null);
          setNote(`${first} now uses the all-clients ${sideCards ? 'side cards' : 'settings'}.`);
          onApplied(v, added);
        },
        base,
      );
    };
    if (changed) setDiscard({ title: 'Discard your unsaved changes?', go });
    else go();
  };
  // ehr#682 (V141 V129): Show Quick add On / Off is saved as soon as it is clicked (the card leaves
  // or comes back to the rail); changes not saved yet stay in the tab.
  const quickShown = !base.hidden;
  const showQuick = (on: boolean) => {
    if (busy || on === quickShown) return;
    put(
      (d) => withShown(d, on),
      (v) => {
        setBase(v.quick ?? EMPTY_QUICK_PREFS);
        setNote(on ? 'Quick add is on.' : 'Quick add is off.');
        onApplied(v, []);
      },
      base,
    );
  };
  // V81 qaReset: this tab back to the practice list (nothing checked, the library's order)
  const reset = () => {
    // ehr#728: on the Side cards tab, the cards on show again (the practice's: all on)
    if (sideCards) {
      setCards((l) => l.filter((k) => !sideCards.cards.includes(k)));
      setNote('Back to the practice list. Save to keep it.');
      return;
    }
    const tabIds = lib.map((i) => i.id);
    setIds((l) => l.filter((x) => !tabIds.includes(x)));
    setDraft((d) => {
      const { [group]: _g, ...order } = d.order ?? {};
      return { ...d, order };
    });
    setNote('Back to the practice list. Save to keep it.');
  };
  const closeDiscard = useCallback(() => setDiscard(null), []);
  // ehr#728: on a Side cards tab, whether this client has that note kind's side cards of their own
  const clientOwn = sideCards
    ? !!base.kindCards?.[sideCards.kind]?.clients?.[contactId]
    : !!base.clients[contactId];
  const noun = tab === 'dot' ? '' : ONE[tab];
  const exact = lib.some((it) => it.label.toLowerCase() === query);

  // ehr#682 (V141 V129, V130): the On / Off rows at the top of the tab
  const onOff = (label: string, on: boolean, set: (on: boolean) => void, test: string) => (
    <div className="pn-qa-sw" data-test={test} key={label}>
      <b>{label}</b>
      <div className="pn-seg pn-ns-seg" role="group" aria-label={label}>
        {(
          [
            [true, 'On'],
            [false, 'Off'],
          ] as const
        ).map(([v, t]) => (
          <button
            key={t}
            type="button"
            aria-pressed={on === v}
            disabled={busy}
            onClick={() => set(v)}
          >
            {t}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <Modal
      title={sideCards ? 'Side cards' : tabs ? 'Quick add' : 'Quick add settings'}
      onClose={requestClose}
      className={tabs ? 'pn-qs-modal pn-ns-modal' : 'pn-qs-modal'}
      actions={
        <div className="pn-qs-foot">
          {clientOwn && (
            <button type="button" className="btn" onClick={useAll} disabled={busy}>
              Use all-clients settings
            </button>
          )}
          {tab !== 'dot' && (
            <button type="button" className="btn" onClick={reset} disabled={busy}>
              Reset to practice list
            </button>
          )}
          <button type="button" className="btn" onClick={() => save('all')} disabled={busy}>
            Save for all clients
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => save('client')}
            disabled={busy}
          >
            {busy ? 'Saving…' : 'Save for this client'}
          </button>
        </div>
      }
    >
      {tabs}
      <div className="pn-qa-sws" data-test="qa-sws">
        {/* ehr#728: the Side cards tab has no Show Quick add, only the note's own cards */}
        {!sideCards &&
          !railOff.includes('quick') &&
          !offCards.includes('quick') &&
          onOff('Show Quick add', quickShown, showQuick, 'qa-sw')}
        {QUICK_SIDE_CARDS.filter(
          ([k]) =>
            !railOff.includes(k) &&
            !offCards.includes(k) &&
            !(noMeasures && k === 'measures') &&
            (!sideCards || sideCards.cards.includes(k)),
        ).map(([k, t]) =>
          onOff(
            `Show ${t}`,
            !cards.includes(k),
            (on) => setCards((l) => (on ? l.filter((x) => x !== k) : [...l, k])),
            'qa-sw-card',
          ),
        )}
      </div>
      {sideCards ? (
        // ehr#728: whose side cards these are, and that they reach this note kind only
        <div className="pn-qs-scope" data-test="side-cards-scope">
          <span>
            {clientOwn
              ? `${first} has side cards of their own.`
              : `${first} uses the all-clients side cards.`}{' '}
            These switches only change {sideCards.kind} notes.
          </span>
        </div>
      ) : (
        <div className="pn-qs-scope">
          <span>
            {clientOwn
              ? `${first} has Quick add settings of their own.`
              : `${first} uses the all-clients settings.`}
          </span>
        </div>
      )}
      {!sideCards && (
        <div className="tc-tabs pn-qs-tabs" role="tablist" aria-label="Quick add settings">
          {QS_TABS.filter(([g]) => !(dotOff && g === 'dot')).map(([g, label]) => (
            <button
              key={g}
              type="button"
              role="tab"
              aria-selected={tab === g}
              className={`tc-tab${tab === g ? ' on' : ''}`}
              onClick={() => {
                if (formTyped && !saveForm()) return;
                pickTab(g);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      {sideCards ? null : tab === 'dot' ? (
        <div className="pn-qs-dot">
          <DotPhrasesPanel view={view} onSaved={(v) => onApplied(v, [])} />
          <p className="pn-dots-help">Each dot phrase is saved when you click its own Save.</p>
        </div>
      ) : (
        <>
          <input
            className="input pn-qs-search"
            value={q}
            placeholder={`Search ${QUICK_GROUP_LABEL[tab].toLowerCase()}`}
            aria-label={`Search ${QUICK_GROUP_LABEL[tab].toLowerCase()}`}
            autoComplete="off"
            onChange={(e) => setQ(e.target.value)}
          />
          <p className="pn-dots-help">
            Checked items are on every new note. <b>Save for this client</b> keeps them for {first};
            <b> Save for all clients</b> uses them for every client without settings of their own.
            {/* ehr#725: a note with no Quick add card has no pills for the rest */}
            {railOff.includes('quick')
              ? ''
              : ' The rest stay as Quick add pills for one note.'}{' '}
            Drag the grip to change the order.
          </p>
          {lib.length > 0 && (
            <div className="pn-qs-sel">
              <span className="pn-sp" />
              <button type="button" className="pn-link" onClick={() => selectAll(true)}>
                Select all
              </button>
              <button type="button" className="pn-link" onClick={() => selectAll(false)}>
                Deselect all
              </button>
            </div>
          )}
          <ul className="pn-qs-list pn-qs-rows" data-test="qs-rows">
            {rows.map((it) => {
              const mine = it.id.startsWith('my_');
              const on = ids.includes(it.id);
              if (del === it.id)
                return (
                  <li key={it.id} className="pn-qs-row pn-qs-del">
                    <span className="pn-qs-name">
                      <b>Delete &ldquo;{it.label}&rdquo;?</b>{' '}
                      <span className="pn-ns-muted">Notes that have it keep it.</span>
                    </span>
                    <button type="button" className="btn small" onClick={() => setDel(null)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn small danger"
                      onClick={() => deleteOwn(it.id)}
                    >
                      Delete
                    </button>
                  </li>
                );
              return (
                <li
                  key={it.id}
                  className={`pn-qs-row${on ? '' : ' off'}${drag === it.id ? ' dragging' : ''}`}
                  onDragOver={(e) => {
                    if (!drag) return;
                    e.preventDefault();
                    if (drag !== it.id)
                      moveTo(
                        drag,
                        lib.findIndex((x) => x.id === it.id),
                      );
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDrag(null);
                  }}
                >
                  <button
                    type="button"
                    className="pn-qs-grip"
                    draggable
                    aria-label={`Move ${it.label} (arrow keys)`}
                    title="Drag to change the order"
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', it.id);
                      setDrag(it.id);
                    }}
                    onDragEnd={() => setDrag(null)}
                    onKeyDown={(e) => {
                      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
                      e.preventDefault();
                      const at = lib.findIndex((x) => x.id === it.id);
                      moveTo(it.id, at + (e.key === 'ArrowUp' ? -1 : 1));
                    }}
                  >
                    {GRIP}
                  </button>
                  <span className="pn-qs-name">
                    <b>{quickLabel(it)}</b>
                    <small>{quickDetail(it)}</small>
                  </span>
                  <span className={`pn-tag${mine ? ' pn-tag-green' : ''}`}>
                    {mine ? 'Mine' : 'Built-in'}
                  </span>
                  {mine && (
                    <>
                      <button
                        type="button"
                        className="pn-link"
                        onClick={() => {
                          setErr('');
                          setDel(null);
                          openForm(it);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="pn-link pn-link-bad"
                        onClick={() => {
                          setForm(null);
                          setDel(it.id);
                        }}
                      >
                        Delete
                      </button>
                    </>
                  )}
                  <label className="pn-qs-on">
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={(e) =>
                        setIds(e.target.checked ? [...ids, it.id] : ids.filter((x) => x !== it.id))
                      }
                    />
                    Add to note
                  </label>
                </li>
              );
            })}
            {rows.length === 0 && (
              <li className="pn-qs-empty">{query ? 'No matches.' : `No ${noun}s yet.`}</li>
            )}
          </ul>
          {!form ? (
            <button
              type="button"
              className="pn-link pn-qs-add"
              onClick={() => {
                setErr('');
                setDel(null);
                openForm();
              }}
            >
              {query && !exact ? `+ Add "${q.trim()}" as my ${noun}` : `+ Add your own ${noun}`}
            </button>
          ) : (
            <div className="pn-dots-form" data-test="qs-form">
              <label>
                <span>Name</span>
                <input
                  value={form.label}
                  maxLength={60}
                  autoFocus
                  placeholder={
                    tab === 'ratings'
                      ? 'Motivation'
                      : tab === 'boxes'
                        ? 'Grief work'
                        : 'Exposure steps'
                  }
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                />
              </label>
              {tab === 'checklists' && (
                <label>
                  <span>Items, one a line</span>
                  <textarea
                    value={form.items}
                    onChange={(e) => setForm({ ...form, items: e.target.value })}
                  />
                </label>
              )}
              {tab === 'ratings' && (
                <div className="pn-qs-scale">
                  <label>
                    <span>From</span>
                    <input
                      type="number"
                      min={0}
                      max={9}
                      value={form.min}
                      onChange={(e) => setForm({ ...form, min: e.target.value })}
                    />
                  </label>
                  <label>
                    <span>To</span>
                    <input
                      type="number"
                      min={1}
                      max={10}
                      value={form.max}
                      onChange={(e) => setForm({ ...form, max: e.target.value })}
                    />
                  </label>
                </div>
              )}
              {tab !== 'ratings' && (
                <label>
                  <span>Where it goes</span>
                  <select
                    value={form.after}
                    onChange={(e) => setForm({ ...form, after: e.target.value as QuickAfter })}
                  >
                    {(Object.keys(AFTER_LABEL) as QuickAfter[]).map((a) => (
                      <option key={a} value={a}>
                        {AFTER_LABEL[a]}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <div className="pn-dots-form-acts">
                <button type="button" className="btn" onClick={() => setForm(null)}>
                  Cancel
                </button>
                <button type="button" className="btn primary" onClick={() => saveForm()}>
                  {form.id ? 'Save' : 'Add'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
      {note && (
        <p className="pn-qs-note" role="status">
          {note}
        </p>
      )}
      {ask && (
        <Modal
          title="Save your changes?"
          onClose={() => setAsk(null)}
          className="pn-qs-ask"
          actions={
            <>
              <button type="button" className="btn" onClick={() => setAsk(null)}>
                Go back
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  const go = ask;
                  setAsk(null);
                  go();
                }}
              >
                Don&rsquo;t save
              </button>
              <button type="button" className="btn" onClick={() => save('all')}>
                Save for all clients
              </button>
              <button type="button" className="btn primary" onClick={() => save('client')}>
                Save for this client
              </button>
            </>
          }
        >
          <p className="pn-modal-p">
            You changed {sideCards ? 'the side cards' : 'Quick add settings'} and haven&rsquo;t
            saved them.
          </p>
        </Modal>
      )}
      {discard && (
        <ConfirmDialog
          message={
            <>
              <b>{discard.title}</b>
              <p className="pn-modal-p">
                Your unsaved changes to {sideCards ? 'the side cards' : 'Quick add settings'} will
                be lost.
              </p>
            </>
          }
          cancelLabel="Go back"
          confirmLabel="Discard"
          onConfirm={discard.go}
          onClose={closeDiscard}
        />
      )}
      {err && (
        <p className="pn-inline-err" role="alert">
          {err}
        </p>
      )}
    </Modal>
  );
}

/** My changes (V48): what was added to or taken off this note, newest first, each with Undo. */
export function MyChangesCard({
  changes,
  onUndo,
  fold,
}: {
  changes: MyChange[];
  onUndo: (i: number) => void;
  /** ehr#437 reopen (V81 rail): folds to its header unless set to stay open. */
  fold?: RailFold;
}) {
  const time = (at: string) => new Date(at).toLocaleTimeString('en-US', timeOpts());
  const shut = !!fold && !fold.open;
  return (
    <section
      className={`pn-card pn-changes-card${shut ? ' pn-rail-shut' : ''}`}
      aria-label="My changes"
    >
      <div className="pn-rail-h">
        <h3>My changes</h3>
        {changes.length > 0 && <span className="pn-tag">{changes.length}</span>}
        <span className="pn-sp" />
        {fold && <RailFoldBtn fold={fold} title="My changes" />}
      </div>
      {!shut && changes.length === 0 && (
        <p className="pn-rail-msg">Nothing added to or taken off this note yet.</p>
      )}
      {!shut && changes.length > 0 && (
        <ul className="pn-changes">
          {changes
            .map((c, i) => [c, i] as const)
            .reverse()
            .map(([c, i]) => (
              <li key={`${c.at}-${i}`}>
                <span>
                  {c.kind === 'add' ? 'Added' : 'Removed'} <b>{quickLabel(c.item)}</b>
                  <em>{time(c.at)}</em>
                </span>
                <button type="button" className="pn-link" onClick={() => onUndo(i)}>
                  Undo
                </button>
              </li>
            ))}
        </ul>
      )}
    </section>
  );
}

type Scope = 'mine' | 'practice';

/** The dot phrases window: your own, and the practice's (editable by admins and managers). */
export function DotPhrasesWindow({
  view,
  onClose,
  onSaved,
}: {
  view: NotePhrasesView;
  onClose: () => void;
  onSaved: (v: NotePhrasesView) => void;
}) {
  return (
    <Modal
      title="Dot phrases"
      onClose={onClose}
      className="pn-dots-modal"
      actions={
        <button type="button" className="btn" onClick={onClose}>
          Done
        </button>
      }
    >
      <DotPhrasesPanel view={view} onSaved={onSaved} />
    </Modal>
  );
}

/**
 * The dot phrases list and its form (in the window above, and ehr#478 the Dot phrases tab of Quick
 * add settings). Each change is saved as it's made.
 */
export function DotPhrasesPanel({
  view,
  onSaved,
}: {
  view: NotePhrasesView;
  onSaved: (v: NotePhrasesView) => void;
}) {
  const [scope, setScope] = useState<Scope>('mine');
  const [lists, setLists] = useState<Record<Scope, DotPhrase[]>>({
    mine: view.mine,
    practice: view.practice,
  });
  const [form, setForm] = useState<{ was: string | null; name: string; text: string } | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const editable = scope === 'mine' || view.canEditPractice;
  // A field or a blank goes in where the cursor is, and the cursor comes back after it.
  const textRef = useRef<HTMLTextAreaElement>(null);
  const insertToken = (tok: string) => {
    if (!form) return;
    const el = textRef.current;
    const a = el ? el.selectionStart : form.text.length;
    const b = el ? el.selectionEnd : form.text.length;
    setForm({ ...form, text: form.text.slice(0, a) + tok + form.text.slice(b) });
    window.requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(a + tok.length, a + tok.length);
    });
  };
  const list = lists[scope];

  const save = (next: DotPhrase[]) => {
    setBusy(true);
    setErr('');
    api
      .put<NotePhrasesView>(`/note-phrases/${scope}`, { phrases: next })
      .then((v) => {
        setLists({ mine: v.mine, practice: v.practice });
        setForm(null);
        onSaved(v);
      })
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : 'Could not save the dot phrases.'),
      )
      .finally(() => setBusy(false));
  };
  const submitForm = () => {
    if (!form) return;
    const name = form.name.trim().replace(/^\./, '').toLowerCase();
    const text = form.text.trim();
    if (!DOT_NAME_RE.test(name))
      return setErr(
        'A name is 2 to 20 lowercase letters, numbers or dashes, starting with a letter.',
      );
    if (!text) return setErr('Type the phrase.');
    if (list.some((p) => p.name === name && p.name !== form.was))
      return setErr(`There is already a .${name}.`);
    const next = form.was
      ? list.map((p) => (p.name === form.was ? { name, text } : p))
      : [...list, { name, text }];
    save(next.sort((a, b) => a.name.localeCompare(b.name)));
  };

  return (
    <>
      <div className="tc-tabs pn-dots-tabs" role="tablist" aria-label="Whose dot phrases">
        {(
          [
            ['mine', 'My dot phrases'],
            ['practice', 'Practice dot phrases'],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={scope === k}
            className={`tc-tab${scope === k ? ' on' : ''}`}
            onClick={() => {
              setScope(k);
              setForm(null);
              setErr('');
            }}
          >
            {label}
          </button>
        ))}
      </div>
      <p className="pn-dots-help">
        {scope === 'mine'
          ? 'Only you see these. One with the same name as a practice phrase replaces it for you.'
          : view.canEditPractice
            ? 'Everyone in the practice can use these.'
            : 'Everyone in the practice can use these. Admins and managers change them.'}
      </p>
      {list.length === 0 && !form && (
        <p className="pn-dots-empty">
          {scope === 'mine' ? 'You have no dot phrases of your own yet.' : 'No practice phrases.'}
        </p>
      )}
      <ul className="pn-dots-list">
        {list.map((p) => (
          <li key={p.name}>
            <b>.{p.name}</b>
            <span>{p.text}</span>
            {editable && (
              <span className="pn-dots-acts">
                <button
                  type="button"
                  className="pn-link"
                  onClick={() => {
                    setErr('');
                    setForm({ was: p.name, name: p.name, text: p.text });
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="pn-link danger"
                  disabled={busy}
                  onClick={() => save(list.filter((x) => x.name !== p.name))}
                >
                  Delete
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
      {editable && !form && (
        <button
          type="button"
          className="btn"
          onClick={() => {
            setErr('');
            setForm({ was: null, name: '', text: '' });
          }}
        >
          + Add a dot phrase
        </button>
      )}
      {form && (
        <div className="pn-dots-form">
          <label>
            <span>Name</span>
            <input
              value={form.name}
              placeholder="sleep"
              maxLength={21}
              aria-label="Dot phrase name"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <label>
            <span>Phrase</span>
            <textarea
              ref={textRef}
              value={form.text}
              placeholder="{client} slept __ hours a night."
              aria-label="Dot phrase text"
              onChange={(e) => setForm({ ...form, text: e.target.value })}
            />
          </label>
          <div className="pn-pills">
            <span className="pn-pills-h">Fill in</span>
            {DOT_FIELDS.map(([k, label]) => (
              <button
                key={k}
                type="button"
                className="pn-pill"
                title={label}
                onClick={() => insertToken(`{${k}}`)}
              >
                {`{${k}}`}
              </button>
            ))}
            <button
              type="button"
              className="pn-pill"
              title="A blank the cursor goes to"
              onClick={() => insertToken('__')}
            >
              __
            </button>
          </div>
          <div className="pn-dots-form-acts">
            <button type="button" className="btn" onClick={() => setForm(null)} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="btn primary" onClick={submitForm} disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
      {err && (
        <p className="pn-inline-err" role="alert">
          {err}
        </p>
      )}
    </>
  );
}
